import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowRight, Loader2, X } from 'lucide-react';

import { POST_LOGIN_REDIRECT_KEY, useAuthStore } from '../../store/authStore';
import { useInvitationStore } from '../../store/invitationStore';

type InviteServerInfo = {
  id: string;
  name: string;
  icon_url: string | null;
  banner_url?: string | null;
  description: string | null;
  member_count: number;
  online_count?: number;
};

function getServerInfo(invitation: any): InviteServerInfo | null {
  return invitation?.server_info || invitation?.server || null;
}

function fadeThroughInviteNavigation(onCovered: () => void) {
  if (typeof document === 'undefined') {
    onCovered();
    return;
  }

  document.querySelectorAll('[data-invite-route-fade="true"]').forEach((node) => node.remove());

  const overlay = document.createElement('div');
  overlay.dataset.inviteRouteFade = 'true';
  overlay.style.position = 'fixed';
  overlay.style.inset = '0';
  overlay.style.zIndex = '99999';
  overlay.style.pointerEvents = 'none';
  overlay.style.background = 'rgb(var(--bg-primary))';
  overlay.style.opacity = '0';
  overlay.style.transition = 'opacity 130ms ease-out';
  document.body.appendChild(overlay);

  requestAnimationFrame(() => {
    overlay.style.opacity = '1';
  });

  window.setTimeout(() => {
    onCovered();

    window.setTimeout(() => {
      overlay.style.transition = 'opacity 220ms cubic-bezier(0.16, 1, 0.3, 1)';
      overlay.style.opacity = '0';
      window.setTimeout(() => overlay.remove(), 260);
    }, 35);
  }, 140);
}

const InviteLink: React.FC = () => {
  const { inviteCode } = useParams<{ inviteCode: string }>();
  const navigate = useNavigate();
  const { user, isLoading: authLoading, startOIDCLogin } = useAuthStore();
  const {
    getInvitationInfo,
    acceptInvitation,
    currentInvitation,
    isLoading,
    error,
    setCurrentInvitation,
  } = useInvitationStore();
  const [joining, setJoining] = useState(false);
  const [joined, setJoined] = useState(false);
  const [openingServer, setOpeningServer] = useState(false);

  useEffect(() => {
    setCurrentInvitation(null);
    setJoined(false);
    setOpeningServer(false);

    if (inviteCode) {
      getInvitationInfo(inviteCode);
    }
  }, [inviteCode, getInvitationInfo, setCurrentInvitation]);

  const serverInfo = useMemo(() => getServerInfo(currentInvitation), [currentInvitation]);
  const onlineCount = serverInfo?.online_count ?? 0;
  const memberCount = serverInfo?.member_count ?? 0;

  const handleClose = () => {
    navigate('/');
  };

  const handleAcceptInvite = async () => {
    if (!inviteCode) return;

    if (!user) {
      localStorage.setItem(POST_LOGIN_REDIRECT_KEY, `/#/invite/${inviteCode}`);
      await startOIDCLogin();
      return;
    }

    setJoining(true);
    const success = await acceptInvitation(inviteCode);
    if (success) {
      setJoined(true);
      setOpeningServer(true);
      setTimeout(() => fadeThroughInviteNavigation(() => navigate('/')), 560);
    }
    setJoining(false);
  };

  if (isLoading && !currentInvitation) {
    return (
      <InviteShell>
        <InvitePanel loading>
          <div className="flex flex-col items-center px-8 py-10 text-center">
            <div className="invite-loading-avatar flex h-20 w-20 items-center justify-center rounded-2xl bg-bg-tertiary shadow-xl shadow-black/30">
              <Loader2 className="h-7 w-7 animate-spin text-accent-color" />
            </div>
            <div className="invite-loading-content mt-7 h-4 w-44 rounded-full bg-bg-tertiary" />
            <div className="invite-loading-content mt-4 h-8 w-56 rounded-lg bg-bg-tertiary/80 [animation-delay:90ms]" />
            <div className="invite-loading-content mt-5 h-4 w-48 rounded-full bg-bg-tertiary/70 [animation-delay:160ms]" />
            <div className="invite-loading-content mt-8 h-11 w-full rounded-lg bg-bg-tertiary/80 [animation-delay:230ms]" />
          </div>
        </InvitePanel>
      </InviteShell>
    );
  }

  if (error || !currentInvitation || !serverInfo) {
    return (
      <InviteShell>
        <InvitePanel>
          <div className="flex flex-col items-center px-8 py-10 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-bg-tertiary text-error">
              <X className="h-7 w-7" />
            </div>
            <h1 className="mt-6 text-xl font-bold text-text-primary">Invalid invitation</h1>
            <p className="mt-2 text-sm text-text-secondary">This invitation is invalid or expired.</p>
            <button
              type="button"
              onClick={handleClose}
              className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-accent-color px-4 py-3 text-sm font-bold text-black transition-colors hover:bg-accent-hover"
            >
              Return to Erozion
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </InvitePanel>
      </InviteShell>
    );
  }

  return (
    <InviteShell bannerUrl={serverInfo.banner_url || undefined} opening={openingServer}>
      <InvitePanel opening={openingServer}>
        <div className="flex flex-col items-center px-8 py-10 text-center">
          <ServerAvatar name={serverInfo.name} iconUrl={serverInfo.icon_url} opening={openingServer} />

          <p className="mt-7 text-sm font-semibold text-text-secondary">You have been invited to join</p>
          <h1 className="mt-2 max-w-full truncate text-2xl font-black tracking-tight text-text-primary">
            {serverInfo.name}
          </h1>

          <div className="mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-sm font-medium text-text-secondary">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
              {onlineCount.toLocaleString()} Online
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-white shadow-[0_0_0_2px_rgba(255,255,255,0.14),0_0_12px_rgba(255,255,255,0.2)]" />
              {memberCount.toLocaleString()} Members
            </span>
          </div>

          <button
            type="button"
            onClick={handleAcceptInvite}
            disabled={joining || joined || authLoading}
            className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-gaming-accent px-4 py-3 text-sm font-bold text-white transition-colors hover:bg-gaming-accent-hover disabled:cursor-not-allowed disabled:opacity-65"
          >
            {joining || authLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {user ? 'Joining server...' : 'Preparing login...'}
              </>
            ) : joined ? (
              'Opening server...'
            ) : (
              'Accept Invite'
            )}
          </button>

          <button
            type="button"
            onClick={handleClose}
            className="mt-4 text-sm font-semibold text-text-secondary transition-colors hover:text-text-primary"
          >
            Cancel
          </button>
        </div>
      </InvitePanel>
    </InviteShell>
  );
};

function ServerAvatar({ name, iconUrl, opening }: { name: string; iconUrl: string | null; opening?: boolean }) {
  const animationClass = `transition-transform duration-500 ease-out ${opening ? 'scale-110' : 'scale-100'}`;

  if (iconUrl) {
    return (
      <div className={`h-20 w-20 overflow-hidden rounded-2xl bg-bg-tertiary shadow-xl shadow-black/30 ${animationClass}`}>
        <img src={iconUrl} alt={name} className="h-full w-full object-cover" />
      </div>
    );
  }

  return (
    <div className={`flex h-20 w-20 items-center justify-center rounded-2xl bg-highlight text-text-primary shadow-xl shadow-black/30 ${animationClass}`}>
      <span className="text-2xl font-semibold">{name.charAt(0).toUpperCase()}</span>
    </div>
  );
}

function InviteShell({
  children,
  bannerUrl,
  opening,
}: {
  children: React.ReactNode;
  bannerUrl?: string;
  opening?: boolean;
}) {
  return (
    <>
      <style>{`
        @keyframes invite-grid-scroll {
          from { background-position: 0 0; }
          to { background-position: 40px 40px; }
        }
        @keyframes invite-open-backdrop {
          from { opacity: 1; transform: scale(1); filter: blur(0); }
          to { opacity: 0.35; transform: scale(1.035); filter: blur(4px); }
        }
        @keyframes invite-open-panel {
          0% { opacity: 1; transform: translateY(0) scale(1); }
          26% { opacity: 1; transform: translateY(-4px) scale(1.015); }
          100% { opacity: 0; transform: translateY(18px) scale(0.94); }
        }
        @keyframes invite-panel-in {
          from { opacity: 0; transform: translateY(18px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes invite-loading-avatar {
          0% { opacity: 0; transform: translateY(10px) scale(0.94); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes invite-loading-sheen {
          0% { opacity: 0.45; transform: translateX(-10px); }
          50% { opacity: 1; }
          100% { opacity: 0.45; transform: translateX(10px); }
        }
        .invite-panel-in {
          animation: invite-panel-in 420ms cubic-bezier(0.16, 1, 0.3, 1) both;
        }
        .invite-loading-avatar {
          animation: invite-loading-avatar 420ms cubic-bezier(0.16, 1, 0.3, 1) both;
        }
        .invite-loading-content {
          animation: invite-loading-sheen 1.35s ease-in-out infinite;
        }
        .invite-opening-backdrop {
          animation: invite-open-backdrop 720ms cubic-bezier(0.16, 1, 0.3, 1) both;
        }
        .invite-opening-panel {
          animation: invite-open-panel 620ms cubic-bezier(0.16, 1, 0.3, 1) both;
        }
      `}</style>
      <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-bg-primary p-4">
        <div className={`absolute inset-0 ${opening ? 'invite-opening-backdrop' : ''}`}>
          {bannerUrl ? (
            <>
              <img src={bannerUrl} alt="" className="absolute inset-0 h-full w-full scale-105 object-cover blur-sm" />
              <div className="absolute inset-0 bg-black/70" />
              <div className="absolute inset-0 bg-bg-primary/35" />
            </>
          ) : (
            <>
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  backgroundImage:
                    'linear-gradient(rgba(249,115,22,0.085) 1px, transparent 1px), linear-gradient(90deg, rgba(249,115,22,0.085) 1px, transparent 1px)',
                  backgroundSize: '40px 40px',
                  animation: 'invite-grid-scroll 12s linear infinite',
                }}
              />
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    'radial-gradient(ellipse 92% 58% at 50% -18%, rgba(251,146,60,0.42) 0%, rgba(249,115,22,0.25) 32%, rgba(249,115,22,0.1) 58%, transparent 82%)',
                }}
              />
              <div
                className="absolute inset-x-0 top-0 h-[58vh] pointer-events-none"
                style={{
                  background:
                    'linear-gradient(180deg, rgba(251,146,60,0.16) 0%, rgba(249,115,22,0.075) 38%, transparent 100%)',
                }}
              />
            </>
          )}
        </div>
        <div className="relative w-full max-w-md">{children}</div>
      </div>
    </>
  );
}

function InvitePanel({
  children,
  opening,
  loading,
}: {
  children: React.ReactNode;
  opening?: boolean;
  loading?: boolean;
}) {
  return (
    <div className={`overflow-hidden rounded-2xl border border-border-color bg-bg-secondary/95 shadow-2xl shadow-black/50 backdrop-blur-md ${opening ? 'invite-opening-panel' : loading ? 'invite-panel-in' : 'invite-panel-in'}`}>
      {children}
    </div>
  );
}

export default InviteLink;
